// src/components/Booklet/BookletSettings.js (Updated with isSaving prop)
import React, { useState, useEffect } from 'react';
import { X, Save, Globe, Lock, Eye, Download, Palette, Loader2 } from 'lucide-react';

const BookletSettings = ({ isOpen, booklet, onClose, onSave, isSaving = false }) => {
  const [settings, setSettings] = useState({
    title: '',
    description: '',
    pageTransition: 'flip',
    theme: 'modern',
    showNavigation: true,
    showPageNumbers: true,
    allowDownload: false,
    backgroundColor: '#ffffff',
    publishSettings: {
      isPublic: false,
      requirePassword: false,
      password: '',
      customDomain: '',
      seoTitle: '',
      seoDescription: '',
      allowAnalytics: true
    }
  });

  useEffect(() => {
    if (isOpen && booklet) {
      console.log('Loading booklet settings:', booklet.settings); // Debug log
      setSettings({
        title: booklet.title || '',
        description: booklet.description || '',
        pageTransition: booklet.settings?.pageTransition || 'flip',
        theme: booklet.settings?.theme || 'modern',
        showNavigation: booklet.settings?.showNavigation ?? true,
        showPageNumbers: booklet.settings?.showPageNumbers ?? true,
        allowDownload: booklet.settings?.allowDownload ?? false,
        backgroundColor: booklet.settings?.backgroundColor || '#ffffff',
        publishSettings: {
          isPublic: booklet.publishSettings?.isPublic ?? false,
          requirePassword: booklet.publishSettings?.requirePassword ?? false,
          password: booklet.publishSettings?.password || '',
          customDomain: booklet.publishSettings?.customDomain || '',
          seoTitle: booklet.publishSettings?.seoTitle || booklet.title || '',
          seoDescription: booklet.publishSettings?.seoDescription || booklet.description || '',
          allowAnalytics: booklet.publishSettings?.allowAnalytics ?? true
        }
      });
    }
  }, [isOpen, booklet]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const updates = {
      title: settings.title,
      description: settings.description,
      settings: {
        pageTransition: settings.pageTransition,
        theme: settings.theme,
        showNavigation: settings.showNavigation,
        showPageNumbers: settings.showPageNumbers,
        allowDownload: settings.allowDownload,
        backgroundColor: settings.backgroundColor
      },
      publishSettings: settings.publishSettings
    };
    
    console.log('Saving settings:', updates); // Debug log
    onSave(updates);
  };

  const updateSetting = (key, value) => {
    console.log('Updating setting:', key, value); // Debug log
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const updatePublishSetting = (key, value) => {
    setSettings(prev => ({
      ...prev,
      publishSettings: {
        ...prev.publishSettings,
        [key]: value
      }
    }));
  };

  const transitions = [
    { id: 'flip', name: 'Page Flip', description: 'Classic book-like page flipping' },
    { id: 'slide', name: 'Slide', description: 'Smooth horizontal sliding' },
    { id: 'fade', name: 'Fade', description: 'Gentle fade transition' },
    { id: 'curl', name: 'Page Curl', description: 'Realistic page curling effect' },
    { id: 'fold', name: 'Accordion Fold', description: 'Accordion-style folding' }
  ];

  const themes = [
    { id: 'modern', name: 'Modern', preview: 'bg-gradient-to-br from-blue-500 to-purple-600' },
    { id: 'classic', name: 'Classic', preview: 'bg-gradient-to-br from-amber-600 to-orange-700' },
    { id: 'minimal', name: 'Minimal', preview: 'bg-gradient-to-br from-gray-600 to-gray-800' },
    { id: 'vibrant', name: 'Vibrant', preview: 'bg-gradient-to-br from-pink-500 to-violet-600' },
    { id: 'nature', name: 'Nature', preview: 'bg-gradient-to-br from-green-600 to-teal-600' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Booklet Settings</h2>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-8">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={settings.title}
                    onChange={(e) => updateSetting('title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    required
                    disabled={isSaving}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background Color
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={settings.backgroundColor}
                      onChange={(e) => updateSetting('backgroundColor', e.target.value)}
                      className="w-12 h-10 border border-gray-300 rounded-lg"
                      disabled={isSaving}
                    />
                    <input
                      type="text"
                      value={settings.backgroundColor}
                      onChange={(e) => updateSetting('backgroundColor', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      disabled={isSaving}
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={settings.description}
                    onChange={(e) => updateSetting('description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    rows={3}
                    disabled={isSaving}
                  />
                </div>
              </div>
            </div>

            {/* Page Transition */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Page Transition</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {transitions.map((transition) => (
                  <div
                    key={transition.id}
                    onClick={() => !isSaving && updateSetting('pageTransition', transition.id)}
                    className={`
                      p-4 border-2 rounded-lg cursor-pointer transition-colors
                      ${settings.pageTransition === transition.id
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                      }
                      ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    <h4 className="font-medium text-gray-900">{transition.name}</h4>
                    <p className="text-sm text-gray-600 mt-1">{transition.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Theme */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Theme</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {themes.map((theme) => (
                  <div
                    key={theme.id}
                    onClick={() => !isSaving && updateSetting('theme', theme.id)}
                    className={`
                      relative p-4 border-2 rounded-lg cursor-pointer transition-colors
                      ${settings.theme === theme.id
                        ? 'border-indigo-500'
                        : 'border-gray-200 hover:border-gray-300'
                      }
                      ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    <div className={`w-full h-16 rounded-lg mb-3 ${theme.preview}`}></div>
                    <p className="text-sm font-medium text-center">{theme.name}</p>
                    {settings.theme === theme.id && (
                      <div className="absolute top-2 right-2 w-4 h-4 bg-indigo-600 rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Display Options */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Display Options</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Show Navigation</label>
                    <p className="text-xs text-gray-500">Display navigation controls and header</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.showNavigation}
                      onChange={(e) => updateSetting('showNavigation', e.target.checked)}
                      className="sr-only peer"
                      disabled={isSaving}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600 peer-disabled:opacity-50"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Show Page Numbers</label>
                    <p className="text-xs text-gray-500">Display current page number</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.showPageNumbers}
                      onChange={(e) => updateSetting('showPageNumbers', e.target.checked)}
                      className="sr-only peer"
                      disabled={isSaving}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600 peer-disabled:opacity-50"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Allow Download</label>
                    <p className="text-xs text-gray-500">Let viewers download the original PDF</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.allowDownload}
                      onChange={(e) => updateSetting('allowDownload', e.target.checked)}
                      className="sr-only peer"
                      disabled={isSaving}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600 peer-disabled:opacity-50"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Publishing Settings */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Publishing Settings</h3>
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Globe className="w-5 h-5 text-gray-600" />
                    <div>
                      <label className="text-sm font-medium text-gray-700">Make Public</label>
                      <p className="text-xs text-gray-500">Allow anyone to view this booklet</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.publishSettings.isPublic}
                      onChange={(e) => updatePublishSetting('isPublic', e.target.checked)}
                      className="sr-only peer"
                      disabled={isSaving}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600 peer-disabled:opacity-50"></div>
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SEO Title
                    </label>
                    <input
                      type="text"
                      value={settings.publishSettings.seoTitle}
                      onChange={(e) => updatePublishSetting('seoTitle', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Title for search engines"
                      disabled={isSaving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Custom Domain
                    </label>
                    <input
                      type="text"
                      value={settings.publishSettings.customDomain}
                      onChange={(e) => updatePublishSetting('customDomain', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="booklet.yourdomain.com"
                      disabled={isSaving}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SEO Description
                  </label>
                  <textarea
                    value={settings.publishSettings.seoDescription}
                    onChange={(e) => updatePublishSetting('seoDescription', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    rows={2}
                    placeholder="Description for search engines"
                    disabled={isSaving}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Enable Analytics</label>
                    <p className="text-xs text-gray-500">Track views and engagement</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.publishSettings.allowAnalytics}
                      onChange={(e) => updatePublishSetting('allowAnalytics', e.target.checked)}
                      className="sr-only peer"
                      disabled={isSaving}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600 peer-disabled:opacity-50"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookletSettings;